import React, { useState, useEffect, useCallback, useRef } from 'react';
import { admin } from '../../services/api';
import QueueStats from './QueueStats';
import AddMedicationModal from './AddMedicationModal';
import BulkImportModal from './BulkImportModal';
import DailyMedSearchModal from './DailyMedSearchModal';
import '../../styles/AdminQueue.css';

function Queue() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    search: '',
  });
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const searchDebounceRef = useRef(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: pagination.limit,
        offset: pagination.offset,
      };

      if (filters.status !== 'all') {
        params.status = filters.status;
      }
      if (filters.priority !== 'all') {
        params.priority = filters.priority;
      }

      const response = await admin.getQueue(params);
      let items = response.data.items || [];

      // Client-side search filter (API doesn't support search yet)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        items = items.filter(item => 
          item.medication_name?.toLowerCase().includes(searchLower) ||
          item.setid?.toLowerCase().includes(searchLower)
        );
      }

      setQueue(items);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || items.length,
      }));
    } catch (err) {
      console.error('Failed to fetch queue:', err);
      alert('Failed to load queue: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, pagination.offset]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Debounce search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    const timeout = setTimeout(() => {
      fetchQueue();
    }, 500);
    
    searchDebounceRef.current = timeout;

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBumpToFront = async (item) => {
    if (!window.confirm(`Move "${item.medication_name}" to the front of its priority tier?`)) {
      return;
    }

    try {
      await admin.bumpToFront(item.id);
      alert('Medication bumped to front!');
      await fetchQueue();
    } catch (err) {
      console.error('Bump failed:', err);
      alert('Failed to bump: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleUrgent = async (item) => {
    const isUrgent = item.priority === 'URGENT';
    
    if (isUrgent) {
      // Remove urgent - use edit modal to let user choose new priority
      if (window.confirm(`Remove URGENT status for "${item.medication_name}"?\n\nYou'll be able to select a new priority.`)) {
        setEditingItem(item);
      }
    } else {
      // Mark as urgent
      if (!window.confirm(`Mark "${item.medication_name}" as URGENT?`)) {
        return;
      }

      try {
        const response = await admin.markAsUrgent(item.id);
        alert(response.data.message || 'Marked as urgent!');
        await fetchQueue();
      } catch (err) {
        console.error('Mark urgent failed:', err);
        alert('Failed to mark urgent: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleRemove = async (item) => {
    if (!window.confirm(`Remove "${item.medication_name}" from queue?`)) {
      return;
    }

    try {
      await admin.removeFromQueue(item.id);
      alert('Removed from queue!');
      await fetchQueue();
    } catch (err) {
      console.error('Remove failed:', err);
      alert('Failed to remove: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    
    try {
      await admin.updateQueueItem(editingItem.id, {
        medication_name: editingItem.medication_name,
        category: editingItem.category,
        drug_class: editingItem.drug_class,
        priority: editingItem.priority,
        notes: editingItem.notes,
      });
      
      alert('Queue item updated!');
      setEditingItem(null);
      await fetchQueue();
    } catch (err) {
      console.error('Update failed:', err);
      alert('Failed to update: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleFilterFailed = () => {
    setFilters(prev => ({ ...prev, status: 'failed', priority: 'all' }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleBulkAddComplete = (results) => {
    console.log('Bulk add complete:', results);
    // Refresh the queue
    fetchQueue();
  };

  const handlePageChange = (direction) => {
    setPagination(prev => ({
      ...prev,
      offset: direction === 'next' 
        ? prev.offset + prev.limit 
        : Math.max(0, prev.offset - prev.limit)
    }));
  };

  const getPriorityClass = (priority) => {
    return `priority-${priority?.toLowerCase().replace('-', '') || 'medium'}`;
  };

  const getStatusClass = (status) => {
    return `status-${status?.toLowerCase() || 'queued'}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // Removed early return for loading state to keep search input always active
  // Loading indicator now shown inside table instead

  return (
    <div className="admin-queue">
      <QueueStats onFilterFailed={handleFilterFailed} />

      <div className="queue-header">
        <h3>Medication Queue</h3>
        <div className="queue-actions">
          <button type="button" className="btn-secondary" onClick={() => setShowAddModal(true)}>
            + Add Medication
          </button>
          <button type="button" className="btn-secondary" onClick={() => setShowBulkModal(true)}>
            📥 Bulk Import
          </button>
        </div>
      </div>

      <div className="queue-filters" style={{ pointerEvents: 'auto', position: 'relative' }}>
        <div className="filter-group">
          <label>Status:</label>
          <select 
            value={filters.status} 
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="all">All</option>
            <option value="queued">Queued</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Priority:</label>
          <select 
            value={filters.priority} 
            onChange={(e) => handleFilterChange('priority', e.target.value)}
          >
            <option value="all">All</option>
            <option value="URGENT">Urgent</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM-HIGH">Medium-High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
        </div>

        <div className="filter-group filter-search">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search by name or SetID..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            disabled={false}
            readOnly={false}
            style={{ pointerEvents: 'auto', opacity: 1 }}
          />
          {loading && filters.search && (
            <span className="search-loading-indicator" style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
              Searching...
            </span>
          )}
        </div>
      </div>

      <div className="queue-table-container">
        <table className="queue-table">
          <thead>
            <tr>
              <th>Medication</th>
              <th>SetID</th>
              <th>Category</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Added</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && queue.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  Loading queue...
                </td>
              </tr>
            ) : queue.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {filters.status !== 'all' || filters.priority !== 'all' || filters.search
                    ? 'No medications match your filters'
                    : 'Queue is empty. Add medications to get started!'}
                </td>
              </tr>
            ) : (
              queue.map(item => (
                <tr key={item.id} className={getStatusClass(item.status)}>
                  <td className="medication-name">
                    <strong>{item.medication_name}</strong>
                    {item.notes && (
                      <div className="item-notes" title={item.notes}>
                        📝 {item.notes.substring(0, 50)}{item.notes.length > 50 ? '...' : ''}
                      </div>
                    )}
                    {item.error_message && (
                      <div className="item-error" title={item.error_message}>
                        ⚠️ {item.error_message.substring(0, 50)}{item.error_message.length > 50 ? '...' : ''}
                      </div>
                    )}
                  </td>
                  <td className="setid-cell">
                    <code>{item.setid}</code>
                  </td>
                  <td>{item.category || '-'}</td>
                  <td>
                    <span className={`priority-badge ${getPriorityClass(item.priority)}`}>
                      {item.priority || 'MEDIUM'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(item.status)}`}>
                      {item.status || 'queued'}
                    </span>
                    {item.retry_count > 0 && (
                      <span className="retry-count">
                        (retry: {item.retry_count})
                      </span>
                    )}
                  </td>
                  <td className="date-cell">{formatDate(item.date_added)}</td>
                  <td className="actions-cell">
                    {item.status === 'queued' && (
                      <>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => handleBumpToFront(item)}
                          title="Bump to front"
                        >
                          ⬆️
                        </button>
                        <button
                          type="button"
                          className={`btn-icon ${item.priority === 'URGENT' ? 'btn-urgent-active' : ''}`}
                          onClick={() => handleToggleUrgent(item)}
                          title={item.priority === 'URGENT' ? 'Remove URGENT status' : 'Mark as urgent'}
                        >
                          {item.priority === 'URGENT' ? '🔴✓' : '🔴'}
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => handleEdit(item)}
                      title="Edit"
                    >
                      📝
                    </button>
                    <button
                      type="button"
                      className="btn-icon btn-danger"
                      onClick={() => handleRemove(item)}
                      title="Remove"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total > pagination.limit && (
        <div className="queue-pagination">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => handlePageChange('prev')}
            disabled={pagination.offset === 0}
          >
            ← Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages} ({pagination.total} total)
          </span>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => handlePageChange('next')}
            disabled={pagination.offset + pagination.limit >= pagination.total}
          >
            Next →
          </button>
        </div>
      )}

      <AddMedicationModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={fetchQueue}
      />

      <BulkImportModal
        show={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSuccess={fetchQueue}
      />

      <DailyMedSearchModal
        show={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onBulkAdd={handleBulkAddComplete}
      />

      {editingItem && (
        <div className="modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="modal-content queue-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Queue Item</h3>
            <form onSubmit={handleSaveEdit}>
              <div className="form-group">
                <label>Medication Name</label>
                <input
                  type="text"
                  value={editingItem.medication_name}
                  onChange={(e) => setEditingItem({...editingItem, medication_name: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  value={editingItem.category || ''}
                  onChange={(e) => setEditingItem({...editingItem, category: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Drug Class</label>
                <input
                  type="text"
                  value={editingItem.drug_class || ''}
                  onChange={(e) => setEditingItem({...editingItem, drug_class: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Priority</label>
                <select
                  value={editingItem.priority}
                  onChange={(e) => setEditingItem({...editingItem, priority: e.target.value})}
                >
                  <option value="URGENT">Urgent</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM-HIGH">Medium-High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={editingItem.notes || ''}
                  onChange={(e) => setEditingItem({...editingItem, notes: e.target.value})}
                  rows="3"
                />
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditingItem(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Queue;
